import { clanChannel } from "../../../shared/chat.js";
import { CLAN_COST, normalizeClanMode } from "../../../shared/clans.js";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import {
  drawCard,
  drawPillButton,
  drawSceneBackground,
  drawSceneTitle,
  fillRoundedRect,
  truncateToWidth,
} from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import type { ClanLeaderboardEntry, ClanMember, ClanMode } from "../systems/Clans";
import { ChatScene } from "./ChatScene";
import { LeaderboardScene } from "./LeaderboardScene";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const CARD_X = 20;
const CARD_W = 320;
const ROW_H = 44;
const ROW_GAP = 6;
const LIST_TOP = 150;
const LIST_BOTTOM = 540;
const LIST_H = LIST_BOTTOM - LIST_TOP;
const CREATE_BTN = { x: CARD_X, y: 102, w: CARD_W, h: 34 };
// Bottom action row: two halves for a member, three thirds for the leader (+Add).
const CHAT_BTN = { x: CARD_X, y: 552, w: 156, h: 36 };
const LEAVE_BTN = { x: CARD_X + 164, y: 552, w: 156, h: 36 };
const CHAT_BTN3 = { x: CARD_X, y: 552, w: 100, h: 36 };
const ADD_BTN3 = { x: CARD_X + 108, y: 552, w: 100, h: 36 };
const LEAVE_BTN3 = { x: CARD_X + 216, y: 552, w: 104, h: 36 };
// Leader-only "change join mode" button, in the header gap above the list.
const MODE_BTN = { x: CARD_X + 70, y: 90, w: 180, h: 26 };
// Bottom Join/Request action when viewing another clan from the leaderboard.
const JOIN_BTN = { x: CARD_X, y: 552, w: CARD_W, h: 36 };

type InClanRow = { kind: "request"; name: string } | { kind: "member"; member: ClanMember };

export class ClanScene implements Scene {
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  private pollAccum = 0;
  private polling = false;
  private acting = false;
  private entered = false;

  // When set, the scene shows a specific clan's roster read-only (opened by tapping
  // a clan tag on the leaderboard) instead of the player's own clan / browse list.
  constructor(
    private readonly ctxMgr: GameContext,
    private readonly viewClanId: number | null = null,
  ) {}

  private get myName(): string {
    return this.ctxMgr.settings.playerName;
  }
  private get token(): string {
    return this.ctxMgr.settings.authToken;
  }
  private get inClan(): boolean {
    return this.ctxMgr.friends.myClan() !== null;
  }
  private get viewing(): boolean {
    return this.viewClanId !== null;
  }

  enter(): void {
    this.entered = true;
    void this.reload();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(dt: number): void {
    if (document.hidden) return;
    this.pollAccum += dt;
    if (this.pollAccum >= 5 && !this.polling && !this.acting) {
      this.pollAccum = 0;
      void this.reload();
    }
  }

  private async reload(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) return;
    this.polling = true;
    if (this.viewClanId !== null) {
      await this.ctxMgr.clans.fetchDetail(this.myName, this.token, this.viewClanId);
    } else {
      const clan = this.ctxMgr.friends.myClan();
      if (clan) await this.ctxMgr.clans.fetchDetail(this.myName, this.token, clan.id);
      else await this.ctxMgr.clans.fetchLeaderboard(this.myName, this.token);
    }
    this.polling = false;
    if (this.entered) this.rebuild();
  }

  // After a membership change, refresh the heartbeat so myClan() flips, then reload.
  private async afterMutation(): Promise<void> {
    await this.ctxMgr.friends.refresh(this.myName, this.token);
    await this.reload();
  }

  private async doCreate(): Promise<void> {
    const raw = window.prompt(
      `Name your clan (letters, digits, spaces; max 20).\nCreating a clan costs ${CLAN_COST} flies.`,
      "",
    );
    if (raw === null) return;
    const clanName = raw.trim();
    if (!clanName) return;
    const rawMode = window.prompt(
      "Join mode — type one:\n• public — anyone can join\n• request — players ask, you approve\n• private — you add members by name",
      "public",
    );
    if (rawMode === null) return;
    const mode = normalizeClanMode(rawMode.trim().toLowerCase()) as ClanMode;
    this.acting = true;
    const res = await this.ctxMgr.clans.create(this.myName, this.token, clanName, mode);
    this.acting = false;
    if (!res.ok) {
      window.alert(
        res.error === "name_taken"
          ? "That clan name is taken."
          : res.error === "already_in_clan"
            ? "You're already in a clan."
            : res.error === "insufficient_flies"
              ? `You need ${CLAN_COST} flies to create a clan.`
              : "Invalid clan name.",
      );
      return;
    }
    await this.ctxMgr.shop.refresh(); // reflect the spent flies
    await this.afterMutation();
  }

  private async doAddMember(): Promise<void> {
    const raw = window.prompt("Add a player to your clan (exact name):", "");
    if (raw === null) return;
    const target = raw.trim();
    if (!target) return;
    this.acting = true;
    const res = await this.ctxMgr.clans.addMember(this.myName, this.token, target);
    this.acting = false;
    if (!res.ok) {
      window.alert(
        res.error === "target_not_found"
          ? `No player named "${target}".`
          : res.error === "target_in_clan"
            ? `${target} is already in a clan.`
            : res.error === "full"
              ? "Your clan is full."
              : "Couldn't add that member.",
      );
    }
    await this.reload();
  }

  private async doJoin(id: number): Promise<void> {
    this.acting = true;
    const res = await this.ctxMgr.clans.join(this.myName, this.token, id);
    this.acting = false;
    if (!res.ok) {
      window.alert(
        res.error === "full"
          ? "That clan is full."
          : res.error === "already_in_clan"
            ? "You're already in a clan."
            : res.error === "private"
              ? "That clan is private — the leader must add you."
              : "Couldn't join that clan.",
      );
    }
    await this.afterMutation();
  }

  private async doLeaveOrDisband(): Promise<void> {
    const clan = this.ctxMgr.friends.myClan();
    if (!clan) return;
    this.acting = true;
    let res;
    if (clan.isLeader) {
      if (!window.confirm(`Disband "${clan.name}"? This deletes the clan and its chat.`)) {
        this.acting = false;
        return;
      }
      res = await this.ctxMgr.clans.disband(this.myName, this.token);
    } else {
      if (!window.confirm(`Leave "${clan.name}"?`)) {
        this.acting = false;
        return;
      }
      res = await this.ctxMgr.clans.leave(this.myName, this.token);
    }
    this.acting = false;
    if (!res.ok && res.error === "leader_must_disband") {
      window.alert("Kick all members first, or disband the clan.");
    }
    await this.afterMutation();
  }

  private async doKick(target: string): Promise<void> {
    if (!window.confirm(`Kick ${target} from the clan?`)) return;
    this.acting = true;
    const res = await this.ctxMgr.clans.kick(this.myName, this.token, target);
    this.acting = false;
    if (!res.ok) window.alert("Couldn't kick that member.");
    await this.reload();
  }

  private async doRequest(id: number): Promise<void> {
    this.acting = true;
    const res = await this.ctxMgr.clans.requestJoin(this.myName, this.token, id);
    this.acting = false;
    if (res.ok) window.alert("Request sent! The clan leader will review it.");
    else if (res.error === "already_in_clan") window.alert("You're already in a clan.");
    else window.alert("Couldn't send the request.");
    await this.reload();
  }

  private async doApprove(target: string): Promise<void> {
    this.acting = true;
    const res = await this.ctxMgr.clans.approve(this.myName, this.token, target);
    this.acting = false;
    if (!res.ok) {
      window.alert(
        res.error === "full"
          ? "Your clan is full."
          : res.error === "target_in_clan"
            ? `${target} already joined another clan.`
            : "Couldn't approve that request.",
      );
    }
    await this.reload();
  }

  private async doDeny(target: string): Promise<void> {
    this.acting = true;
    await this.ctxMgr.clans.deny(this.myName, this.token, target);
    this.acting = false;
    await this.reload();
  }

  private async doChangeMode(): Promise<void> {
    const current = this.ctxMgr.clans.clanDetail()?.mode ?? "public";
    const raw = window.prompt(
      `Change join mode (currently ${current}) — type one:\n• public — anyone can join\n• request — players ask, you approve\n• private — you add members by name`,
      current,
    );
    if (raw === null) return;
    const mode = normalizeClanMode(raw.trim().toLowerCase()) as ClanMode;
    this.acting = true;
    await this.ctxMgr.clans.setMode(this.myName, this.token, mode);
    this.acting = false;
    await this.afterMutation();
  }

  private openChat(): void {
    const clan = this.ctxMgr.friends.myClan();
    if (!clan) return;
    this.ctxMgr.goTo(
      new ChatScene(this.ctxMgr, clanChannel(clan.id), clan.name, () =>
        this.ctxMgr.goTo(new ClanScene(this.ctxMgr)),
      ),
    );
  }

  // Visible-row hit rects (Join for clans, kick × for members).
  private rowButton(rowY: number, kind: "join" | "kick"): { x: number; y: number; w: number; h: number } {
    const cy = rowY + (ROW_H - 26) / 2;
    return kind === "join"
      ? { x: CARD_X + CARD_W - 64, y: cy, w: 58, h: 26 }
      : { x: CARD_X + CARD_W - 32, y: cy, w: 26, h: 26 };
  }
  private approveRect(rowY: number): { x: number; y: number; w: number; h: number } {
    return { x: CARD_X + CARD_W - 64, y: rowY + (ROW_H - 26) / 2, w: 28, h: 26 };
  }
  private denyRect(rowY: number): { x: number; y: number; w: number; h: number } {
    return { x: CARD_X + CARD_W - 32, y: rowY + (ROW_H - 26) / 2, w: 26, h: 26 };
  }
  // In-clan list = pending requests (leader-only) above the member roster.
  private inClanRows(): InClanRow[] {
    const d = this.ctxMgr.clans.clanDetail();
    const out: InClanRow[] = [];
    for (const n of d?.requests ?? []) out.push({ kind: "request", name: n });
    for (const m of d?.members ?? []) out.push({ kind: "member", member: m });
    return out;
  }

  private rebuild(): void {
    this.recomputeScroll();
    // Viewing another clan (opened from the leaderboard): back goes to the
    // leaderboard, plus a Join/Request action if eligible.
    if (this.viewing) {
      const buttons: Button[] = [
        backButton(() => this.ctxMgr.goTo(new LeaderboardScene(this.ctxMgr, this.ctxMgr.renderer))),
      ];
      const d = this.ctxMgr.clans.clanDetail();
      const canAct = d && !this.inClan && (d.mode === "public" || d.mode === "request");
      if (canAct) {
        const label = d.mode === "request" ? "Request to join" : "Join clan";
        buttons.push({
          ...JOIN_BTN,
          draw: (ctx, hit) => drawWideButton(ctx, label, JOIN_BTN, hit, "#2e7d32"),
          onPress: () =>
            d.mode === "request" ? void this.doRequest(d.id) : void this.doJoin(d.id),
        });
      }
      this.ctxMgr.buttons.set(buttons);
      return;
    }
    const buttons: Button[] = [
      backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr))),
    ];

    if (this.ctxMgr.settings.isLoggedIn()) {
      if (this.inClan) {
        const clan = this.ctxMgr.friends.myClan();
        if (clan?.isLeader) {
          // Leader row: Chat | + Add member | Disband.
          buttons.push(
            {
              ...CHAT_BTN3,
              draw: (ctx, hit) => drawWideButton(ctx, "Chat", CHAT_BTN3, hit, "#2e7d32"),
              onPress: () => this.openChat(),
            },
            {
              ...ADD_BTN3,
              draw: (ctx, hit) => drawWideButton(ctx, "+ Add", ADD_BTN3, hit, "#7a5e2a"),
              onPress: () => void this.doAddMember(),
            },
            {
              ...LEAVE_BTN3,
              draw: (ctx, hit) => drawWideButton(ctx, "Disband", LEAVE_BTN3, hit, "#8e1a1a"),
              onPress: () => void this.doLeaveOrDisband(),
            },
          );
        } else {
          buttons.push(
            {
              ...CHAT_BTN,
              draw: (ctx, hit) => drawWideButton(ctx, "Clan chat", CHAT_BTN, hit, "#2e7d32"),
              onPress: () => this.openChat(),
            },
            {
              ...LEAVE_BTN,
              draw: (ctx, hit) => drawWideButton(ctx, "Leave", LEAVE_BTN, hit, "#8e1a1a"),
              onPress: () => void this.doLeaveOrDisband(),
            },
          );
        }
        // Leader: a "change join mode" button in the header gap.
        if (clan?.isLeader) {
          const mode = this.ctxMgr.clans.clanDetail()?.mode ?? "public";
          buttons.push({
            ...MODE_BTN,
            draw: (ctx, hit) => drawWideButton(ctx, `Mode: ${mode}  ✎`, MODE_BTN, hit, "#3a4256"),
            onPress: () => void this.doChangeMode(),
          });
        }
        // Per-row buttons: approve/deny for request rows, kick × for members.
        if (clan?.isLeader) {
          const stride = ROW_H + ROW_GAP;
          this.inClanRows().forEach((row, i) => {
            const rowY = LIST_TOP + i * stride - this.scrollY;
            if (rowY < LIST_TOP || rowY + ROW_H > LIST_BOTTOM) return;
            if (row.kind === "request") {
              const a = this.approveRect(rowY);
              const d = this.denyRect(rowY);
              buttons.push({ ...a, draw: () => undefined, onPress: () => void this.doApprove(row.name) });
              buttons.push({ ...d, draw: () => undefined, onPress: () => void this.doDeny(row.name) });
            } else if (row.member.name.toLowerCase() !== this.myName.toLowerCase()) {
              const r = this.rowButton(rowY, "kick");
              buttons.push({ ...r, draw: () => undefined, onPress: () => void this.doKick(row.member.name) });
            }
          });
        }
      } else {
        buttons.push({
          ...CREATE_BTN,
          draw: (ctx, hit) =>
            drawWideButton(ctx, `+ Create a clan (${CLAN_COST} flies)`, CREATE_BTN, hit, "#7a5e2a"),
          onPress: () => void this.doCreate(),
        });
        this.board().forEach((c, i) => {
          if (c.mode === "private") return; // private clans aren't publicly joinable
          const rowY = LIST_TOP + i * (ROW_H + ROW_GAP) - this.scrollY;
          if (rowY < LIST_TOP || rowY + ROW_H > LIST_BOTTOM) return;
          const r = this.rowButton(rowY, "join");
          const onPress =
            c.mode === "request"
              ? () => void this.doRequest(c.id)
              : () => void this.doJoin(c.id);
          buttons.push({ ...r, draw: () => undefined, onPress });
        });
      }
    }

    this.ctxMgr.buttons.set(buttons);
  }

  private board(): ClanLeaderboardEntry[] {
    return this.ctxMgr.clans.leaderboardEntries();
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "CLANS");

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.fillStyle = "#f5e9c8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Log in to join a clan.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    if (this.viewing) {
      this.renderViewMode(ctx);
      return;
    }

    const clan = this.ctxMgr.friends.myClan();
    const inClanRows = clan ? this.inClanRows() : [];
    const rows = clan ? inClanRows.length : this.board().length;
    ctx.textAlign = "center";
    ctx.font = "12px system-ui, sans-serif";
    if (clan) {
      const mode = this.ctxMgr.clans.clanDetail()?.mode ?? "public";
      ctx.fillStyle = "#f5e9c8";
      ctx.fillText(
        truncateToWidth(ctx, `${clan.name}${clan.isLeader ? "  (leader)" : ""}`, CARD_W),
        VIRTUAL_WIDTH / 2,
        62,
      );
      // Leaders get the tappable MODE_BTN (drawn via the button layer); members
      // just see the current mode as text.
      if (!clan.isLeader) {
        ctx.fillStyle = "#9fb0c8";
        ctx.fillText(`Join mode: ${mode}`, VIRTUAL_WIDTH / 2, 100);
      }
      ctx.fillStyle = "#9fb0c8";
      const pending = this.ctxMgr.clans.clanDetail()?.requests?.length ?? 0;
      ctx.fillText(
        pending > 0 ? `${pending} pending request${pending === 1 ? "" : "s"} · members below` : "Members ranked by best score",
        VIRTUAL_WIDTH / 2,
        130,
      );
    } else {
      // Browse: subtitle + fly balance sit ABOVE the Create button (which is at
      // y=102) so nothing overlaps the list (LIST_TOP=150).
      ctx.fillStyle = "#9fb0c8";
      ctx.fillText("Top clans by members' combined scores", VIRTUAL_WIDTH / 2, 66);
      ctx.fillStyle = "#f0d98a";
      ctx.fillText(`You have ${this.ctxMgr.shop.wallet} flies`, VIRTUAL_WIDTH / 2, 88);
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, LIST_TOP, CARD_W + 4, LIST_H);
    ctx.clip();
    const stride = ROW_H + ROW_GAP;
    if (rows === 0) {
      ctx.fillStyle = "#8a93a8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        clan ? "Just you so far." : "No clans yet — create the first!",
        VIRTUAL_WIDTH / 2,
        LIST_TOP + 30,
      );
    }
    for (let i = 0; i < rows; i += 1) {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      if (rowY + ROW_H < LIST_TOP || rowY > LIST_BOTTOM) continue;
      if (clan) {
        const row = inClanRows[i]!;
        if (row.kind === "request") this.drawRequestRow(ctx, row.name, rowY);
        else this.drawMemberRow(ctx, row.member, rowY);
      } else {
        this.drawClanRow(ctx, this.board()[i]!, rowY);
      }
    }
    ctx.restore();
  }

  // Read-only roster of another clan (opened from the leaderboard).
  private renderViewMode(ctx: CanvasRenderingContext2D): void {
    const d = this.ctxMgr.clans.clanDetail();
    ctx.textAlign = "center";
    if (!d) {
      ctx.fillStyle = "#8a93a8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Loading clan…", VIRTUAL_WIDTH / 2, LIST_TOP + 30);
      return;
    }
    ctx.fillStyle = "#f5e9c8";
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillText(truncateToWidth(ctx, d.name, CARD_W), VIRTUAL_WIDTH / 2, 62);
    ctx.fillStyle = "#9fb0c8";
    ctx.fillText(
      `${d.members.length} member${d.members.length === 1 ? "" : "s"} · ${d.mode} clan`,
      VIRTUAL_WIDTH / 2,
      100,
    );

    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, LIST_TOP, CARD_W + 4, LIST_H);
    ctx.clip();
    const stride = ROW_H + ROW_GAP;
    if (d.members.length === 0) {
      ctx.fillStyle = "#8a93a8";
      ctx.font = "13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No members.", VIRTUAL_WIDTH / 2, LIST_TOP + 30);
    }
    d.members.forEach((m, i) => {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      if (rowY + ROW_H < LIST_TOP || rowY > LIST_BOTTOM) return;
      this.drawMemberRow(ctx, m, rowY);
    });
    ctx.restore();

    // Note beside where the Join/Request button (button layer) would be.
    if (this.inClan || d.mode === "private") {
      ctx.fillStyle = "#8a7a4a";
      ctx.font = "12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        this.inClan ? "You're already in a clan." : "🔒 Private — invite only.",
        VIRTUAL_WIDTH / 2,
        JOIN_BTN.y + 23,
      );
    }
  }

  private drawRequestRow(ctx: CanvasRenderingContext2D, name: string, rowY: number): void {
    drawCard(ctx, CARD_X, rowY, CARD_W, ROW_H, 10, { border: "#2e7d32" });
    ctx.fillStyle = "#1a1f2b";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(truncateToWidth(ctx, name, 180), CARD_X + 12, rowY + 15);
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillStyle = "#2e7d32";
    ctx.fillText("wants to join", CARD_X + 12, rowY + 31);
    ctx.textBaseline = "alphabetic";
    drawPillButton(ctx, "✓", "#2e7d32", this.approveRect(rowY));
    drawPillButton(ctx, "✗", "#8e1a1a", this.denyRect(rowY));
  }

  private drawMemberRow(ctx: CanvasRenderingContext2D, m: ClanMember, rowY: number): void {
    rowCard(ctx, rowY);
    const dotX = CARD_X + 16;
    const dotY = rowY + ROW_H / 2;
    ctx.fillStyle = m.online ? "#2ecc40" : "#8a8a8a";
    ctx.beginPath();
    ctx.arc(dotX, dotY, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#1a1f2b";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(m.name, CARD_X + 30, rowY + ROW_H / 2);
    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = "#7a5e2a";
    ctx.textAlign = "right";
    // No kick affordance while viewing another clan (you're not its leader here).
    const leader = !this.viewing && this.ctxMgr.friends.myClan()?.isLeader;
    const scoreRight = leader ? CARD_X + CARD_W - 40 : CARD_X + CARD_W - 12;
    ctx.fillText(`best ${m.score}`, scoreRight, rowY + ROW_H / 2);
    ctx.textBaseline = "alphabetic";

    if (leader && m.name.toLowerCase() !== this.myName.toLowerCase()) {
      const r = this.rowButton(rowY, "kick");
      drawPillButton(ctx, "×", "#8e1a1a", r);
    }
  }

  private drawClanRow(ctx: CanvasRenderingContext2D, c: ClanLeaderboardEntry, rowY: number): void {
    rowCard(ctx, rowY);
    ctx.fillStyle = "#1a1f2b";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(`${c.rank}. ${c.name}${c.mode === "private" ? " 🔒" : ""}`, CARD_X + 12, rowY + 15);
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillStyle = "#7a5e2a";
    ctx.fillText(`${c.members} member${c.members === 1 ? "" : "s"} · ${c.score} pts`, CARD_X + 12, rowY + 32);
    ctx.textBaseline = "alphabetic";
    if (c.mode === "private") {
      ctx.textAlign = "right";
      ctx.fillStyle = "#8a7a4a";
      ctx.font = "11px system-ui, sans-serif";
      ctx.fillText("private", CARD_X + CARD_W - 12, rowY + ROW_H / 2);
    } else if (c.mode === "request") {
      drawPillButton(ctx, "Request", "#7a5e2a", this.rowButton(rowY, "join"));
    } else {
      drawPillButton(ctx, "Join", "#2e7d32", this.rowButton(rowY, "join"));
    }
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
    this.recomputeScroll();
    this.rebuild();
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    const { y } = this.ctxMgr.renderer.clientToCanvas(e.clientX, e.clientY);
    if (y >= LIST_TOP && y <= LIST_BOTTOM) this.dragLastY = e.clientY;
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.ctxMgr.renderer.canvas.getBoundingClientRect();
    const dy = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dy, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };

  private recomputeScroll(): void {
    const rows = this.viewing
      ? (this.ctxMgr.clans.clanDetail()?.members.length ?? 0)
      : this.inClan
        ? this.inClanRows().length
        : this.board().length;
    this.maxScroll = Math.max(0, rows * (ROW_H + ROW_GAP) - ROW_GAP - LIST_H);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;
  }
}

function rowCard(ctx: CanvasRenderingContext2D, rowY: number): void {
  drawCard(ctx, CARD_X, rowY, CARD_W, ROW_H, 10);
}

function drawWideButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  r: { x: number; y: number; w: number; h: number },
  hit: boolean,
  color: string,
): void {
  ctx.fillStyle = hit ? "#f5e9c8" : color;
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.fillStyle = hit ? color : "#fff";
  ctx.font = "bold 15px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
